import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus, AlertCircle, Loader2 } from 'lucide-react';
import {
  cachedQuery, fmtValue, fmtDim, isDateDim, colorFor, OTHER_COLOR, MAX_SERIES, SERIES_COLORS,
  resolveRange, parseRef, makeCtx,
} from './lib';
import { DimValue, Logo } from './Fields';

// ─── Sorgu kurulumu ──────────────────────────────────────────────────────────
function daysBetween(start, end) {
  return Math.round((new Date(end) - new Date(start)) / 86400000) + 1;
}

// Gösterilecek referanslardan parça başına istenecek metrikleri ve formülleri çıkarır.
function payloadFor(w, refs) {
  const calcs = [];
  const need = new Map();
  const add = (ref) => {
    const r = parseRef(ref);
    if (r.calc) {
      const c = (w.calcs || []).find((x) => x.id === r.calc);
      if (!c || calcs.some((x) => x.id === c.id)) return;
      calcs.push({ id: c.id, a: c.a, op: c.op, b: c.b });
      add(c.a); add(c.b);
      return;
    }
    if (!r.partId || !r.metric) return;
    if (!need.has(r.partId)) need.set(r.partId, new Set());
    need.get(r.partId).add(r.metric);
  };
  refs.forEach(add);
  const parts = (w.parts || [])
    .filter((p) => p.source && need.has(p.id))
    .map((p) => ({ id: p.id, source: p.source, metrics: [...need.get(p.id)], filters: (p.filters || []).filter((f) => f.dimension && f.value) }));
  return { parts, calcs };
}

export function buildQueries(w, range) {
  const { start, end } = resolveRange(w.dateMode === 'custom' ? w.dateRange : range);
  const metrics = (w.metrics || []).filter(Boolean);
  if (!metrics.length) return null;
  const single = ['kpi', 'pie', 'pivot'].includes(w.type) || (w.breakdown && w.type !== 'table');
  const refs = single ? metrics.slice(0, 1) : metrics;
  const { parts, calcs } = payloadFor(w, refs);
  if (!parts.length) return null;
  const base = { parts, calcs, start, end };
  switch (w.type) {
    case 'kpi':
      return [{
        ...base, dimensions: [daysBetween(start, end) > 120 ? 'date_week' : 'date_day'],
        compare: w.compare && w.compare !== 'none' ? w.compare : undefined, limit: 5000,
      }];
    case 'line':
    case 'area':
    case 'stacked':
      return [{ ...base, dimensions: [w.dimension, w.breakdown].filter(Boolean), limit: 5000 }];
    case 'bar':
    case 'hbar': {
      const many = w.breakdown || isDateDim(w.dimension);
      return [{ ...base, dimensions: [w.dimension, w.breakdown].filter(Boolean), limit: many ? 5000 : (w.limit || 10) }];
    }
    case 'pie':
      return [{ ...base, dimensions: [w.dimension], limit: 5000 }];
    case 'table':
      return [{
        ...base, dimensions: [w.dimension, w.dimension2].filter(Boolean), limit: w.limit || 25,
        sort: w.sort?.key ? w.sort : undefined,
      }];
    case 'pivot':
      if (!w.pivotRow || !w.pivotCol || w.pivotRow === w.pivotCol) return null;
      return [
        { ...base, dimensions: [w.pivotRow, w.pivotCol], limit: 5000 },
        { ...base, dimensions: [w.pivotRow], limit: 5000 },
        { ...base, dimensions: [w.pivotCol], limit: 5000 },
      ];
    default:
      return null;
  }
}

function useWidgetData(brandId, queries, refreshKey) {
  const key = queries ? JSON.stringify(queries) : null;
  const [state, setState] = useState({ loading: !!key, error: null, data: null });
  useEffect(() => {
    if (!key || !brandId) { setState({ loading: false, error: null, data: null }); return undefined; }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.all(JSON.parse(key).map((q) => cachedQuery(brandId, q, ctrl.signal)))
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => { if (err.name !== 'AbortError') setState({ loading: false, error: err.message, data: null }); });
    return () => ctrl.abort();
  }, [brandId, key, refreshKey]);
  return state;
}

// ─── Ortak parçalar ──────────────────────────────────────────────────────────
function Centered({ children }) {
  return <div className="h-full w-full flex items-center justify-center text-sm text-gray-400 px-4 text-center">{children}</div>;
}

function ChartTooltip({ active, payload, label, fmtLabel, fmtVal }) {
  if (!active || !payload?.length) return null;
  const items = [...payload].filter((p) => p.value !== null && p.value !== undefined).sort((a, b) => (b.value || 0) - (a.value || 0));
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs min-w-[160px]">
      <div className="font-medium text-gray-900 mb-1">{fmtLabel(label)}</div>
      {items.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color || p.payload?.fill }} />
            {p.name}
          </span>
          <span className="font-medium text-gray-900 tabular-nums">{fmtVal(p.value, p.dataKey)}</span>
        </div>
      ))}
    </div>
  );
}

const AXIS = { fontSize: 11, fill: '#6b7280' };

// rows → recharts verisi + seri listesi.
// Kırılım varsa en büyük 7 seri tutulur, kalanı toplanabilir metrikte "Diğer"e katlanır.
function shapeChart(rows, w, ctx, { topX } = {}) {
  const metrics = w.breakdown ? w.metrics.filter(Boolean).slice(0, 1) : w.metrics.filter(Boolean);
  const dim = w.dimension;
  if (!w.breakdown) {
    let data = rows.map((r) => ({ x: r.dims[0], ...Object.fromEntries(metrics.map((m) => [m, r[m]])) }));
    // Birleşik kaynaklarda yalnızca bir tarafta olan kategoriler (ör. GA4'teki "Direct") çizilecek değer taşımaz.
    if (!isDateDim(dim)) data = data.filter((d) => metrics.some((m) => d[m] !== null && d[m] !== undefined));
    if (isDateDim(dim)) data.sort((a, b) => (a.x < b.x ? -1 : 1));
    else if (topX) data = data.slice(0, topX);
    return {
      data,
      series: metrics.map((m, i) => ({ key: m, name: ctx.metric(m).fullLabel, color: SERIES_COLORS[i], metric: m })),
    };
  }
  const m = metrics[0];
  const meta = ctx.metric(m);
  const totals = new Map();
  rows.forEach((r) => totals.set(r.dims[1], (totals.get(r.dims[1]) || 0) + (r[m] || 0)));
  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const keep = new Set(ranked.slice(0, MAX_SERIES));
  const fold = !meta.derived && ranked.length > MAX_SERIES;

  const byX = new Map();
  const xTotals = new Map();
  rows.forEach((r) => {
    const x = r.dims[0];
    let s = r.dims[1];
    if (!keep.has(s)) { if (!fold) return; s = 'Other'; }
    if (!byX.has(x)) byX.set(x, { x });
    const o = byX.get(x);
    o[s] = (o[s] || 0) + (r[m] || 0);
    xTotals.set(x, (xTotals.get(x) || 0) + (r[m] || 0));
  });
  let data = [...byX.values()];
  if (isDateDim(dim)) data.sort((a, b) => (a.x < b.x ? -1 : 1));
  else {
    data.sort((a, b) => (xTotals.get(b.x) || 0) - (xTotals.get(a.x) || 0));
    if (topX) data = data.slice(0, topX);
  }
  const names = ranked.slice(0, MAX_SERIES);
  if (fold) names.push('Other');
  return {
    data,
    series: names.map((n, i) => ({ key: n, name: n, color: colorFor(n, i), metric: m })),
  };
}

function legendFormatter(value) {
  return <span className="text-xs text-gray-600">{value}</span>;
}

// ─── Çizgi / alan / sütun ────────────────────────────────────────────────────
function CartesianWidget({ w, ctx, data: [res], currency }) {
  const topX = ['bar', 'hbar', 'stacked'].includes(w.type) && !isDateDim(w.dimension) ? (w.limit || 10) : undefined;
  const { data, series } = useMemo(() => shapeChart(res.rows, w, ctx, { topX }), [res, w, ctx, topX]);
  if (!data.length) return <Centered>No data in this range</Centered>;

  const fmtOf = (key) => ctx.metric(series.find((s) => s.key === key)?.metric || key).fmt;
  const fmtVal = (v, key) => fmtValue(v, fmtOf(key), currency);
  const fmtTick = (v) => fmtValue(v, fmtOf(series[0]?.key), currency, true);
  const fmtLabel = (x) => fmtDim(x, w.dimension);
  const tooltip = <Tooltip content={<ChartTooltip fmtLabel={fmtLabel} fmtVal={fmtVal} />} cursor={{ fill: 'rgba(17,24,39,0.04)', stroke: '#d1d5db' }} />;
  const legend = series.length >= 2 ? <Legend iconType="circle" iconSize={8} formatter={legendFormatter} wrapperStyle={{ paddingTop: 4 }} /> : null;
  const grid = <CartesianGrid stroke="#f1f5f9" vertical={false} />;
  const margin = { top: 8, right: 12, bottom: 0, left: 0 };

  if (w.type === 'hbar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={margin} barCategoryGap="20%">
          <CartesianGrid stroke="#f1f5f9" horizontal={false} />
          <XAxis type="number" tick={AXIS} tickFormatter={fmtTick} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="x" tick={AXIS} width={130} tickFormatter={(x) => { const s = fmtLabel(x); return s.length > 20 ? `${s.slice(0, 19)}…` : s; }} axisLine={false} tickLine={false} />
          {tooltip}{legend}
          {series.map((s) => (
            <Bar isAnimationActive={false} key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[0, 4, 4, 0]} stackId={w.breakdown ? 'a' : undefined} stroke="#fff" strokeWidth={w.breakdown ? 1 : 0} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  const xAxis = <XAxis dataKey="x" tick={AXIS} tickFormatter={(x) => { const s = fmtLabel(x); return s.length > 14 ? `${s.slice(0, 13)}…` : s; }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} minTickGap={16} interval={isDateDim(w.dimension) ? 'preserveEnd' : 0} />;
  const yAxis = <YAxis tick={AXIS} tickFormatter={fmtTick} axisLine={false} tickLine={false} width={60} />;

  if (w.type === 'line') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={margin}>
          {grid}{xAxis}{yAxis}{tooltip}{legend}
          {series.map((s) => (
            <Line isAnimationActive={false} key={s.key} dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={data.length <= 14 ? { r: 3, strokeWidth: 0, fill: s.color } : false} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (w.type === 'area') {
    const stacked = !!w.breakdown;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={margin}>
          {grid}{xAxis}{yAxis}{tooltip}{legend}
          {series.map((s) => (
            <Area isAnimationActive={false} key={s.key} dataKey={s.key} name={s.name} stroke={s.color} fill={s.color} fillOpacity={stacked ? 0.85 : 0.15} strokeWidth={2} stackId={stacked ? 'a' : undefined} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  const stack = w.type === 'stacked' || (w.type === 'bar' && w.breakdown);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={margin} barCategoryGap="20%">
        {grid}{xAxis}{yAxis}{tooltip}{legend}
        {series.map((s, i) => (
          <Bar isAnimationActive={false} key={s.key} dataKey={s.key} name={s.name} fill={s.color} stackId={stack ? 'a' : undefined}
            radius={stack ? (i === series.length - 1 ? [4, 4, 0, 0] : 0) : [4, 4, 0, 0]} stroke="#fff" strokeWidth={stack ? 1 : 0} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Halka ───────────────────────────────────────────────────────────────────
function PieWidget({ w, ctx, data: [res], currency }) {
  const m = w.metrics[0];
  const meta = ctx.metric(m);
  const slices = useMemo(() => {
    const rows = [...res.rows].filter((r) => (r[m] || 0) > 0).sort((a, b) => (b[m] || 0) - (a[m] || 0));
    const n = Math.min(w.limit || 6, MAX_SERIES);
    const top = rows.slice(0, n).map((r, i) => ({ name: r.dims[0], value: r[m], color: colorFor(r.dims[0], i) }));
    const rest = rows.slice(n).reduce((a, r) => a + (r[m] || 0), 0);
    if (rest > 0) top.push({ name: 'Other', value: rest, color: OTHER_COLOR });
    return top;
  }, [res, m, w.limit]);
  if (meta.derived) return <Centered>Donut charts don't work with ratio metrics; pick an additive metric.</Centered>;
  if (!slices.length) return <Centered>No data in this range</Centered>;
  const total = slices.reduce((a, s) => a + s.value, 0);
  return (
    <div className="h-full w-full flex items-center gap-3">
      <div className="h-full flex-1 min-w-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="90%" paddingAngle={1} stroke="#fff" strokeWidth={2} isAnimationActive={false}>
              {slices.map((s) => <Cell key={s.name} fill={s.color} />)}
            </Pie>
            <Tooltip content={<ChartTooltip fmtLabel={() => meta.label} fmtVal={(v) => `${fmtValue(v, meta.fmt, currency)} · ${((v / total) * 100).toFixed(1)}%`} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="text-xs space-y-1.5 max-w-[50%] overflow-auto max-h-full pr-1">
        {slices.map((s) => (
          <li key={s.name} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-gray-700 min-w-0 flex">{s.name === 'Other' ? 'Other' : <DimValue dimKey={w.dimension} value={s.name} />}</span>
            <span className="ml-auto pl-2 text-gray-500 tabular-nums">{((s.value / total) * 100).toFixed(0)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── KPI ─────────────────────────────────────────────────────────────────────
function KpiWidget({ w, ctx, data: [res], currency }) {
  const m = w.metrics[0];
  const meta = ctx.metric(m);
  const value = res.totals?.[m];
  const prev = res.compareTotals?.[m];
  const delta = prev ? (value - prev) / Math.abs(prev) : null;
  const good = delta === null ? null : (meta.inverse ? delta < 0 : delta > 0);
  const spark = res.rows.map((r) => ({ x: r.dims[0], v: r[m] }));
  const Icon = delta === null || Math.abs(delta) < 0.0005 ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <div className="h-full flex flex-col">
      <div className="text-3xl font-semibold text-gray-900 tabular-nums tracking-tight">{fmtValue(value, meta.fmt, currency)}</div>
      {w.compare && w.compare !== 'none' && (
        <div className="mt-1 flex items-center gap-1.5 text-xs">
          {delta === null ? (
            <span className="text-gray-400">No comparison data</span>
          ) : (
            <>
              <span className={`inline-flex items-center gap-0.5 font-medium rounded px-1.5 py-0.5 ${good ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
                <Icon className="w-3.5 h-3.5" />{Math.abs(delta * 100).toFixed(1)}%
              </span>
              <span className="text-gray-500">{w.compare === 'yoy' ? 'vs last year' : 'vs previous period'} ({fmtValue(prev, meta.fmt, currency)})</span>
            </>
          )}
        </div>
      )}
      {spark.length > 1 && (
        <div className="flex-1 min-h-[24px] mt-2 -mx-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <Tooltip content={<ChartTooltip fmtLabel={(x) => fmtDim(x, 'date_day')} fmtVal={(v) => fmtValue(v, meta.fmt, currency)} />} />
              <Area dataKey="v" name={meta.label} stroke={SERIES_COLORS[0]} fill={SERIES_COLORS[0]} fillOpacity={0.12} strokeWidth={2} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Tablo ───────────────────────────────────────────────────────────────────
// Aynı metrik iki kaynakta varsa (örn. iki "Oturum") başlıkta kaynak adı da yazılır.
function sameLabelTwice(w, ctx, ref) {
  const label = ctx.metric(ref).label;
  return w.metrics.filter((x) => ctx.metric(x).label === label).length > 1;
}

function TableWidget({ w, ctx, data: [res], currency, onChange }) {
  const dims = [w.dimension, w.dimension2].filter(Boolean);
  const metrics = w.metrics.filter(Boolean);
  const sort = w.sort || {};
  const setSort = (key) => {
    const dir = sort.key === key && sort.dir === 'desc' ? 'asc' : 'desc';
    onChange({ sort: { key, dir } });
  };
  const arrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');
  if (!res.rows.length) return <Centered>No data in this range</Centered>;
  return (
    <div className="h-full overflow-auto -mx-4 -mb-4">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr className="text-gray-600">
            {dims.map((d) => (
              <th key={d} className="text-left font-medium px-4 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => setSort(d)}>{ctx.dim(d).label}{arrow(d)}</th>
            ))}
            {metrics.map((m) => (
              <th key={m} className="text-right font-medium px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => setSort(m)} title={ctx.metric(m).fullLabel}>
                <span className="inline-flex items-center gap-1.5 justify-end">
                  {ctx.multi && <Logo name={ctx.metric(m).isCalc ? 'calc' : ctx.metric(m).logo} sourceKey={ctx.metric(m).sourceKey} className="w-3.5 h-3.5" />}
                  {sameLabelTwice(w, ctx, m) ? ctx.metric(m).fullLabel : ctx.metric(m).label}{arrow(m)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {res.rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100 hover:bg-blue-50/40">
              {dims.map((d, j) => (
                <td key={d} className="px-4 py-1.5 text-gray-800 max-w-[280px]"><div className="flex min-w-0"><DimValue dimKey={d} value={r.dims[j]} /></div></td>
              ))}
              {metrics.map((m) => (
                <td key={m} className="px-3 py-1.5 text-right tabular-nums text-gray-900">{fmtValue(r[m], ctx.metric(m).fmt, currency)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 bg-gray-50">
          <tr className="border-t border-gray-200 font-semibold text-gray-900">
            <td className="px-4 py-2" colSpan={dims.length}>Total{res.truncated ? ` (showing first ${res.rows.length} rows)` : ''}</td>
            {metrics.map((m) => (
              <td key={m} className="px-3 py-2 text-right tabular-nums">{fmtValue(res.totals?.[m], ctx.metric(m).fmt, currency)}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─── Pivot ───────────────────────────────────────────────────────────────────
// Hücreler [satır×sütun] sorgusundan, satır/sütun toplamları ayrı sorgulardan gelir:
// oran metriklerinde (ROAS, CTR…) toplam hücrelerin toplamı değildir.
const MAX_PIVOT_COLS = 15;

function PivotWidget({ w, ctx, data: [cellsRes, rowRes, colRes], currency }) {
  const m = w.metrics[0];
  const meta = ctx.metric(m);
  const { rows, cols, cells, rowTotals, colTotals, hiddenCols, max } = useMemo(() => {
    const colTotalMap = new Map(colRes.rows.map((r) => [r.dims[0], r[m]]));
    let colKeys = [...colTotalMap.keys()];
    if (isDateDim(w.pivotCol)) colKeys.sort();
    else colKeys.sort((a, b) => (colTotalMap.get(b) || 0) - (colTotalMap.get(a) || 0));
    const hidden = Math.max(0, colKeys.length - MAX_PIVOT_COLS);
    colKeys = colKeys.slice(0, MAX_PIVOT_COLS);
    const rowTotalMap = new Map(rowRes.rows.map((r) => [r.dims[0], r[m]]));
    const rowKeys = [...rowTotalMap.keys()];
    if (isDateDim(w.pivotRow)) rowKeys.sort();
    const cellMap = new Map();
    let mx = 0;
    cellsRes.rows.forEach((r) => {
      cellMap.set(`${r.dims[0]}\u0000${r.dims[1]}`, r[m]);
      if (r[m] > mx) mx = r[m];
    });
    return { rows: rowKeys, cols: colKeys, cells: cellMap, rowTotals: rowTotalMap, colTotals: colTotalMap, hiddenCols: hidden, max: mx };
  }, [cellsRes, rowRes, colRes, m, w.pivotRow, w.pivotCol]);

  if (!rows.length) return <Centered>No data in this range</Centered>;
  const shade = (v) => {
    if (!w.heatmap || v === null || v === undefined || max <= 0) return undefined;
    const t = Math.max(0, Math.min(1, v / max));
    return `rgba(42, 120, 214, ${0.06 + t * 0.5})`;
  };
  const f = (v) => fmtValue(v, meta.fmt, currency);
  return (
    <div className="h-full overflow-auto -mx-4 -mb-4">
      <table className="text-xs border-separate border-spacing-0 min-w-full">
        <thead className="sticky top-0 z-20">
          <tr className="bg-gray-50 text-gray-600">
            <th className="sticky left-0 z-30 bg-gray-50 text-left font-medium px-4 py-2 border-b border-gray-200 whitespace-nowrap">
              {ctx.dim(w.pivotRow).label} \ {ctx.dim(w.pivotCol).label}
            </th>
            {cols.map((c) => (
              <th key={c} className="text-right font-medium px-3 py-2 border-b border-gray-200 whitespace-nowrap max-w-[180px]"><div className="flex justify-end min-w-0"><DimValue dimKey={w.pivotCol} value={c} /></div></th>
            ))}
            <th className="text-right font-semibold px-3 py-2 border-b border-l border-gray-200 bg-gray-100 text-gray-800">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r} className="hover:bg-blue-50/40">
              <td className="sticky left-0 z-10 bg-white px-4 py-1.5 border-b border-gray-100 text-gray-800 whitespace-nowrap max-w-[260px]"><div className="flex min-w-0"><DimValue dimKey={w.pivotRow} value={r} /></div></td>
              {cols.map((c) => {
                const v = cells.get(`${r}\u0000${c}`);
                return (
                  <td key={c} className="px-3 py-1.5 border-b border-gray-100 text-right tabular-nums text-gray-900" style={{ background: shade(v) }}>{v === undefined ? <span className="text-gray-300">—</span> : f(v)}</td>
                );
              })}
              <td className="px-3 py-1.5 border-b border-l border-gray-100 text-right tabular-nums font-semibold text-gray-900 bg-gray-50">{f(rowTotals.get(r))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 z-20">
          <tr className="bg-gray-100 font-semibold text-gray-900">
            <td className="sticky left-0 z-30 bg-gray-100 px-4 py-2 border-t border-gray-200">Total</td>
            {cols.map((c) => <td key={c} className="px-3 py-2 border-t border-gray-200 text-right tabular-nums">{f(colTotals.get(c))}</td>)}
            <td className="px-3 py-2 border-t border-l border-gray-200 text-right tabular-nums">{f(cellsRes.totals?.[m])}</td>
          </tr>
        </tfoot>
      </table>
      {hiddenCols > 0 && <div className="px-4 py-2 text-[11px] text-gray-500">Showing the top {MAX_PIVOT_COLS} columns; {hiddenCols} hidden (included in the grand total).</div>}
    </div>
  );
}

// ─── Metin ───────────────────────────────────────────────────────────────────
const TEXT_SIZE = { sm: 'text-sm', base: 'text-base', lg: 'text-lg', xl: 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl' };
const TEXT_ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
const V_ALIGN = { top: 'justify-start', middle: 'justify-center', bottom: 'justify-end' };
export const TEXT_COLORS = {
  dark: '#111827', muted: '#4b5563', blue: '#1d4ed8', green: '#047857', red: '#be123c', white: '#ffffff',
};

// Eski kayıtlar textStyle (heading/note) taşıyor; yeni alanlar yoksa ondan türetilir.
export function textProps(w) {
  const heading = (w.textStyle || 'heading') === 'heading';
  return {
    size: w.textSize || (heading ? 'xl' : 'sm'),
    bold: w.textBold ?? heading,
    italic: !!w.textItalic,
    align: w.textAlign || 'left',
    vAlign: w.textVAlign || 'middle',
    color: w.textColor || (heading ? 'dark' : 'muted'),
  };
}

export function TextWidget({ w }) {
  const t = textProps(w);
  const text = w.text || '';
  if (!text.trim()) return <Centered>Edit the widget to add text</Centered>;
  return (
    <div className={`h-full w-full overflow-auto flex flex-col ${V_ALIGN[t.vAlign]}`}>
      <div className={`whitespace-pre-wrap leading-snug ${TEXT_SIZE[t.size] || 'text-base'} ${TEXT_ALIGN[t.align]} ${t.bold ? 'font-semibold' : 'font-normal'} ${t.italic ? 'italic' : ''}`}
        style={{ color: TEXT_COLORS[t.color] || t.color }}>
        {text}
      </div>
    </div>
  );
}

// ─── Gövde ───────────────────────────────────────────────────────────────────
export function WidgetBody({ w, sourcesByKey, brandId, range, currency, refreshKey, onChange }) {
  const queries = useMemo(() => (w.type === 'text' ? null : buildQueries(w, range)), [w, range]);
  const { loading, error, data } = useWidgetData(brandId, queries, refreshKey);
  const ctx = useMemo(() => makeCtx(w, sourcesByKey), [w, sourcesByKey]);

  if (w.type === 'text') return <TextWidget w={w} />;
  const parts = w.parts || [];
  if (!parts.length || parts.some((p) => !p.source)) return <Centered>No source selected</Centered>;
  if (!Object.keys(sourcesByKey).length) return <Centered><Loader2 className="w-5 h-5 animate-spin" /></Centered>;
  const off = parts.map((p) => sourcesByKey[p.source]).find((s) => s && !s.connected);
  if (off) return <Centered>{off.label} is not connected for this brand</Centered>;
  if (!queries) return <Centered>Finish setting up this widget</Centered>;
  if (error) return <Centered><span className="inline-flex items-center gap-1.5 text-rose-600"><AlertCircle className="w-4 h-4 shrink-0" />{error}</span></Centered>;
  if (!data) return <Centered><Loader2 className="w-5 h-5 animate-spin" /></Centered>;

  const props = { w, ctx, data, currency, onChange };
  let body;
  if (w.type === 'kpi') body = <KpiWidget {...props} />;
  else if (w.type === 'pie') body = <PieWidget {...props} />;
  else if (w.type === 'table') body = <TableWidget {...props} />;
  else if (w.type === 'pivot') body = <PivotWidget {...props} />;
  else body = <CartesianWidget {...props} />;
  return (
    <div className={`h-full w-full transition-opacity ${loading ? 'opacity-50' : ''}`}>{body}</div>
  );
}
